import React, { useEffect, useState } from 'react';

// Explicit request (EnquiryFormDrawer): renders `children` only once `targetNode` has scrolled out from under the
// sticky bar at the top of `rootNode` (the drawer's scroll container).
//
// This used to be a boolean in the drawer's own state - every time the header card crossed the threshold (so on
// practically every scroll up / down) the whole drawer re-rendered, and with it the Activity timeline, the
// Follow Up form and so on. The observer and its state now live here, so a scroll only re-renders this small
// component (the breadcrumb in the header bar).
interface ShowWhenScrolledPastProps {
    isEnabled: boolean;
    rootNode: HTMLElement | null;
    targetNode: HTMLElement | null;
    // the bar height the target must slide under before it counts as "scrolled past" (px)
    barHeight?: number;
}

export const ShowWhenScrolledPast: React.FC<ShowWhenScrolledPastProps> = ({ isEnabled, rootNode, targetNode, barHeight = 44, children }) => {
    const [isScrolledPast, setIsScrolledPast] = useState(false);

    // Observes the target against the drawer's own Paper as root (not window - the page scrolls inside that
    // Paper, not the browser viewport). Depends on the nodes (set via callback refs) so it re-runs once they
    // actually exist after the drawer's loading skeleton clears.
    useEffect(() => {
        if (!isEnabled || !rootNode || !targetNode) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry) {
                    setIsScrolledPast(!entry.isIntersecting);
                }
            },
            { root: rootNode, rootMargin: `-${barHeight}px 0px 0px 0px` },
        );
        observer.observe(targetNode);
        return () => observer.disconnect();
    }, [isEnabled, rootNode, targetNode, barHeight]);

    return isScrolledPast ? <>{children}</> : null;
};
